// note: this needs to stay in sync with the server side routes as defined
// in AppController.kt

import { ConsumerDetailPageParams } from "../pages/consumer/ConsumerDetailPage";
import { formatUrl } from "../../common/root/apiRoutes";

const rep = (original: string, params: any) => {
  return original
    .replace(":id", params.id)
    .replace(":orgId", params.orgId)
    .replace(":organizationId", params.organizationId)
    .replace(":email", params.email)
    .replace(":requestId", params.requestId)
    .replace(":vendorId", params.vendorId)
    .replace(":mailbox", params.mailbox)
    .replace(":privacyCenterId", params.privacyCenterId)
    .replace(":regulationSlug", params.regulationSlug)
    .replace(":tabId", params.tabId)
    .replace(":dataRequestTypeId", params.dataRequestTypeId)
    .replace(":dataSubjectTypeId", params.dataSubjectTypeId)
    .replace(":groupId", params.groupId)
    .replace(":consumerGroupId", params.consumerGroupId)
    .replace(":pageIndex", params.pageIndex)
    .replace(":category", params.category)
    .replace(":sectionId", params.sectionId)
    .replace(":introId", params.introId)
    .replace(":consumerId", params.consumerId)
    .replace(":dataSourceId", params.dataSourceId)
    .replace(":dataRecipientId", params.dataRecipientId)
    .replace(":dpaId", params.dpaId)
    .replace(":conversationId", params.conversationId);
};

export const Routes = {
  root: "/",
  login: "/login",
  reset: "/reset",
  auth: {
    login: "/login",
    mfa: "/mfa",
    mfaValidate: "/mfa/validate",
    reset: "/reset",
    set: "/reset/:resetToken",
  },
  dashboard: {
    root: "/dashboard",
  },
  integrations: {
    shopify: "/integrations/shopify",
  },
  invite: "/invite/:organizationId",
  requests: {
    active: "/requests/active",
    closed: "/requests/closed",
    pending: "/requests/pending",
    detail: "/requests/:requestId",
    tutorials: "/requests/tutorials",
    privacyEmails: "/requests/privacy-emails",
    privacyEmailConversation: "/requests/privacy-emails/:conversationId",
    privacyEmailSettings: "/requests/privacy-email-settings",
  },
  workflows: {
    dashboard: "/workflows/dashboard",
    dataStorageSettings: "/workflows/data-storage-settings",
    emailTemplates: "/workflows/email-templates",
  },
  records: {
    recordLookup: "/records/lookup",
    websiteConsentRecords: "/records/website-consent",
    consumerConsentLookup: "/records/consumer-consent-lookup",
    gpcOptOuts: "/records/gpc-opt-outs",
  },
  consumer: {
    detail: "/consumer",
  },
  tasks: {
    open: "/todo/open",
    completed: "/todo/completed",
  },
  privacyCenter: {
    root: "/privacy-center",
    dashboard: "/privacy-center/dashboard",
    privacyPolicy: "/privacy-center/privacy-policy",
    usStates: "/privacy-center/us-states",
    internationalNotices: "/privacy-center/international-notices",
    cookiePolicy: "/privacy-center/cookie-policy",
    financialIncentives: "/privacy-center/financial-incentives",
    dataRetention: "/privacy-center/data-retention",
    publish: "/privacy-center/publish",
    editPrivacySection: "/privacy-center/privacy-sections/:sectionId",
    addPrivacySection: "/privacy-center/privacy-sections/add",
    editPrivacyIntro: "/privacy-center/privacy-intro/:introId",
    settings: "/privacy-center/settings",
    privacyEmails: "/privacy-center/settings/privacy-emails",
    theme: "/privacy-center/theme",
    previewPopup: "/privacy-center/preview-popup",
    consentBanner: "/privacy-center/consent-banner",
    integrations: "/privacy-center/integrations",
    requestForms: "/privacy-center/request-forms",
    // `/request-forms` is renamed from its previous name `/dsar-forms`, so keep the old URL for backwards compatibility
    dsarForms: "/privacy-center/dsar-forms",
  },
  ramsay: {
    root: "/ramsay",
    serviceAccountInbox: "/ramsay/service-account-inbox",
    serviceAccountSetup: "/ramsay/service-account-setup",
  },
  hrCenter: {
    root: "/hr-center",
    editHrRetentionPolicy: "/hr-center/hr-data-retention",
    hrNotices: "/hr-center/hr-notices",
    coloradoBiometricNotice: "/hr-center/hr-notices/colorado-biometric-identifiers",
    hrVendorInventory: "/hr-center/hr-vendor-inventory",
    hrVendorDocumentation: "/hr-center/hr-vendor-documentation",
    hrDsarWorkflows: "/hr-center/hr-dsar-workflows",
    hrDataMap: {
      root: "/hr-center/hr-data-map",
      employeeSources: "/hr-center/hr-data-map/employee-sources",
      retentionSchedule: "/hr-center/hr-data-map/retention-schedule",
      dataFlows: "/hr-center/hr-data-map/data-flows",
      dataFlowsCollection: "/hr-center/hr-data-map/data-flows/collection/:groupId",
      dataFlowsDisclosures: "/hr-center/hr-data-map/data-flows/disclosures/:groupId",
      dataFlowsPurposes: "/hr-center/hr-data-map/data-flows/purposes/:groupId",
    },
  },
  notices: {
    root: "/notices",
    financialIncentives: "/notices/financial-incentives",
    dataRetention: "/notices/data-retention",
    publish: "/notices/publish",
    editPrivacySection: "/notices/privacy-sections/:sectionId",
    addPrivacySection: "/notices/privacy-sections/add",
    editPrivacyIntro: "/notices/privacy-intro/:introId",
  },
  knowledgeCenter: {
    root: "/knowledge-center",
  },
  documents: {
    root: "/my-documents",
    recordExports: "/my-documents/record-exports",
    dataProtectionAssessments: "/my-documents/data-protection-assessments",
    pendingDPA: "/my-documents/data-protection-assessments/pending",
    historyDPA: "/my-documents/data-protection-assessments/history",
    dataProtectionAssessment: {
      write: "/my-documents/data-protection-assessments/:dpaId/write",
      addApprovalParties: "/my-documents/data-protection-assessments/:dpaId/addParties",
      gatherSignatures: "/my-documents/data-protection-assessments/:dpaId/gatherSignatures",
    },
    recordOfProcessingActivities: {
      home: "/my-documents/record-of-processing-activities",
      history: "/my-documents/record-of-processing-activities/history",
      edit: "/my-documents/record-of-processing-activities/edit",
    },
    hrDataRetentionPolicies: "/my-documents/hr-data-retention-policies",
    privacyPolicies: "/my-documents/privacy-policies",
    auditLogs: "/my-documents/audit-logs",
    myFiles: "/my-documents/my-files",
  },
  cookies: {
    root: "/cookies",
  },
  websiteAudit: {
    root: "/website-audit",
  },
  trueScan: {
    root: "/truescan",
  },
  dataRecipients: {
    root: "/data-recipients",
    add: "/data-recipients/add",
    addCustom: "/data-recipients/add/custom",
    addThirdParty: "/data-recipients/add/custom?thirdParty=1",
    addDetail: "/data-recipients/add/:vendorId",
    detail: "/data-recipients/:vendorId",
    edit: "/data-recipients/:vendorId/edit",
  },
  vendors: {
    root: "/vendors",
    catalog: "/vendors/catalog",
    new: "/vendors/new",
    detail: "/vendors/:vendorId",
  },
  dataMap: {
    dashboard: "/data-map/dashboard",
    root: "/data-map",
    dataRetention: "/data-map/data-retention",
    vendorDocumentation: "/data-map/vendor-documentation",
    vendorInventory: "/data-map/vendor-inventory",
    dataFlows: {
      dashboard: "/data-map/data-flows",
      sources: "/data-map/data-flows/sources",
      collection: "/data-map/data-flows/collection/:groupId",
      disclosures: "/data-map/data-flows/disclosures/:groupId",
      purposes: "/data-map/data-flows/purposes",
    },
    lawfulBases: {
      detail: "/data-map/lawful-bases",
    },
    collectionGroup: {
      workflow: {
        collection: "/data-map/collection/new/:dataSubjectTypeId/collection",
        dataRecipientAssociation: "/data-map/collection/new/:dataSubjectTypeId/association",
        disclosure: "/data-map/collection/new/:dataSubjectTypeId/disclosure",
        sourceAssociation: "/data-map/collection/new/:dataSubjectTypeId/source-association",
        receipt: "/data-map/collection/new/:dataSubjectTypeId/receipt",
      },
    },
    dataRecipient: {
      detail: "/data-map/recipients/:dataRecipientId",
      new: "/data-map/recipients/new",
      workflow: {
        classification: "/data-map/recipients/new/:dataRecipientId/classification",
      },
    },
    dataSource: {
      detail: "/data-map/sources/:dataSourceId",
      new: "/data-map/sources/new",
      workflow: {
        groupAssociation: "/data-map/sources/new/:dataSourceId/association",
        receipt: "/data-map/sources/new/:dataSourceId/receipt",
      },
    },
  },
  organization: {
    jwt: "/organization/:organizationId/jwt",
    eventPulse: "/organization/:organizationId/event-pulse",
    settings: {
      root: "/organization/:organizationId/settings",
      users: "/organization/:organizationId/settings/users",
      billing: "/organization/:organizationId/settings/billing",
      domain: "/organization/:organizationId/settings/domain",
      advanced: "/organization/:organizationId/settings/advanced",
      businessSurvey: "/business-survey-settings",
    },
    requestHandling: {
      base: "/organization/requestHandling",
      consumerGroup: "/organization/requestHandling/consumerGroup",
      accessRequest: "/organization/requestHandling/accessRequest",
      requestToKnow: "/organization/requestHandling/requestToKnow",
      requestToDelete: "/organization/requestHandling/requestToDelete",
      requestToCorrection: "/organization/requestHandling/requestToCorrection",
      requestToAppeal: "/organization/requestHandling/requestToAppeal",
      requestToWithdrawalOfConsent: "/organization/requestHandling/requestToWithdrawalOfConsent",
      requestToProfilingInformation: "/organization/requestHandling/requestToProfilingInformation",
      requestToPortability: "/organization/requestHandling/requestToPortability",
      requestToObjection: "/organization/requestHandling/requestToObjection",
      requestToRestrictProcessing: "/organization/requestHandling/requestToRestrictProcessing",
      requestToOptOut: "/organization/requestHandling/requestToOptOut",
      requestToProfilingOptOut: "/organization/requestHandling/requestToProfilingOptOut",
      requestToLimit: "/organization/requestHandling/requestToLimit",
      instructions: "/organization/:organizationId/requestHandling/:regulationSlug", // TODO: can this be eliminated yet?
    },
  },

  user: {
    profile: "/user/profile",
  },

  // Admin Routes
  admin: {
    csComplianceDashboard: "/admin/cs-compliance-dashboard",
  },

  print: {
    privacyNotices: {
      root: "/print/privacy-notices",
      caPrivacyNotice: "/print/privacy-notices/california-privacy-notice",
      optOutPrivacyNotice: "/print/privacy-notices/opt-out-privacy-notice",
      consumerGroup: "/print/privacy-notices/consumer-group/:collectionGroupId",
    },
  },

  // Get Compliant Phase Routes
  getCompliant: {
    root: "/get-compliant",
    dashboard: "/get-compliant/dashboard",
    inviteUsers: "/get-compliant/invite-users",
    businessSurveyAquilo: "/get-compliant/business-survey-aquilo",
    dataMap: {
      consumerSources: "/get-compliant/data-map/sources",
    },
    employeeMap: {
      hrSurvey: "/get-compliant/employee-map/hr-survey",
      introduction: "/get-compliant/employee-map/introduction",

      employee: "/get-compliant/employee-map/employee",
      employeeCollectionForGroup: "/get-compliant/employee-map/employee/:consumerGroupId",
      employeeAssociationForGroup: "/get-compliant/employee-map/employee/:groupId/association",
      employeeDisclosureForGroup: "/get-compliant/employee-map/employee/:groupId/disclosure",
      employeeReviewForGroup: "/get-compliant/employee-map/employee/:consumerGroupId/review",
      dataRecipients: "/get-compliant/employee-map/data-recipients",
      dataDisclosures: "/get-compliant/employee-map/data-disclosures",
      dataSources: "/get-compliant/employee-map/collection-sources",
      eeaDataSources: "/get-compliant/employee-map/eea-collection-sources",
      dataStorage: "/get-compliant/employee-map/data-storage",
      hrRetentionPolicy: "/get-compliant/employee-map/hr-retention-policy",
      employmentNoticeReview: "/get-compliant/employee-map/notice-review",
      deletionExceptions: "/get-compliant/employee-map/exceptions",
    },
    dataRecipients: {
      root: "/get-compliant/data-recipients",

      dataDisclosures: "/get-compliant/data-recipients/data-disclosures",
      dataDisclosuresForGroup: "/get-compliant/data-recipients/data-disclosures/:groupId",

      thirdPartyRecipients: "/get-compliant/data-recipients/third-party-recipients",
      selling: "/get-compliant/data-recipients/selling",
      sharing: "/get-compliant/data-recipients/sharing",
      consumerGroups: "/get-compliant/data-recipients/consumer-groups",
      consumerGroup: "/get-compliant/data-recipients/consumer-groups/:consumerGroupId",
      classifyServices: "/get-compliant/data-recipients/classify-services",
      locateOther: "/get-compliant/data-recipients/locate-other",
      reviewVendorContracts: "/get-compliant/data-recipients/review-vendor-contracts",
      classifyOther: "/get-compliant/data-recipients/classify-other",
    },
    dataCollection: {
      root: "/get-compliant/data-collection",
      consumerCollection: "/get-compliant/data-collection/consumer",
      consumerPurposes: "/get-compliant/data-collection/consumer/purposes",
      corePrivacyConcepts: "/get-compliant/data-collection/consumer/core-privacy-concepts",
      consumerIncentives: "/get-compliant/data-collection/consumer/incentives",
      gdprLawfulBases: "/get-compliant/data-collection/gdpr/lawful-bases",
      additionalPrivacyLanguage:
        "/get-compliant/data-collection/consumer/additional-privacy-language",
      internationalDataTransfers:
        "/get-compliant/data-recipients/gdpr/international-data-transfers",
      contactProcessors: "/get-compliant/data-recipients/gdpr/contact-processors",
    },
    dataRetention: {
      root: "/get-compliant/data-retention",
      vendorIntegrations: "/get-compliant/data-retention/integrations",
      retentionPolicy: "/get-compliant/data-retention/retention-policy",
      gdprCookieScanner: "/get-compliant/data-retention/cookie-scanner",
      cookiePolicy: "/get-compliant/data-retention/cookie-policy",
    },
    privacyNotices: {
      root: "/get-compliant/privacy-notices",
    },
    publishNotices: {
      root: "/get-compliant/publish-notices",
    },
    privacyRequests: {
      root: "/get-compliant/privacy-requests",
      processing:
        "/get-compliant/privacy-requests/:consumerGroupId/request-type/:dataRequestTypeId",
      privacyCenter: "/get-compliant/privacy-requests/privacy-center",
    },
    privacyCenter: {
      domainSetup: "/get-compliant/privacy-center/domain-setup",
      verifySubdomain: "/get-compliant/privacy-center/verify-subdomain",
    },
    settings: {
      root: "/get-compliant/settings",
      user: "/get-compliant/settings/user",
    },
  },

  // Instructions
  instructions: {
    id: "/instructions/:id",
    emailDomainSetup: "/instructions/:id/email-domain-setup",
  },
};

export const frontendPathToUrl = (path: string) => {
  return `${window.location.origin}${path}`;
};

export const RouteHelpers = {
  instructions: {
    id: (id) => rep(Routes.instructions.id, { id }),
    emailDomainSetup: (id) => rep(Routes.instructions.emailDomainSetup, { id }),
  },
  getCompliant: {
    vendors: {
      consumerGroup: (consumerGroupId) =>
        rep(Routes.getCompliant.dataRecipients.consumerGroup, { consumerGroupId }),
    },
    dataRecipients: {
      dataDisclosuresForGroup: (groupId) =>
        rep(Routes.getCompliant.dataRecipients.dataDisclosuresForGroup, { groupId }),
    },
    employeeMap: {
      employeeCollectionForGroup: (consumerGroupId) =>
        rep(Routes.getCompliant.employeeMap.employeeCollectionForGroup, { consumerGroupId }),
      employeeAssociationForGroup: (groupId) =>
        rep(Routes.getCompliant.employeeMap.employeeAssociationForGroup, { groupId }),
      employeeDisclosureForGroup: (groupId) =>
        rep(Routes.getCompliant.employeeMap.employeeDisclosureForGroup, { groupId }),
      employeeNoticeReviewForGroup: (consumerGroupId) =>
        rep(Routes.getCompliant.employeeMap.employeeReviewForGroup, { consumerGroupId }),
    },
    privacyRequests: {
      processing: (consumerGroupId, dataRequestTypeId) =>
        rep(Routes.getCompliant.privacyRequests.processing, {
          consumerGroupId,
          dataRequestTypeId,
        }),
    },
  },
  print: {
    privacyNotices: {
      consumerGroup: (consumerGroupId) =>
        rep(Routes.print.privacyNotices.consumerGroup, { consumerGroupId }),
    },
  },
  organization: {
    settings: (organizationId) => rep(Routes.organization.settings.root, { organizationId }),
    eventPulse: (organizationId) => rep(Routes.organization.eventPulse, { organizationId }),
    users: (organizationId) => rep(Routes.organization.settings.users, { organizationId }),
    domain: (organizationId) => rep(Routes.organization.settings.domain, { organizationId }),
    advanced: (organizationId) => rep(Routes.organization.settings.advanced, { organizationId }),
    requestHandling: {
      base: (organizationId) => rep(Routes.organization.requestHandling.base, { organizationId }),
      instructions: (organizationId, regulationSlug) =>
        rep(Routes.organization.requestHandling.instructions, {
          organizationId,
          regulationSlug,
        }),
    },
    billing: (organizationId) => rep(Routes.organization.settings.billing, { organizationId }),
  },
  invite: (organizationId) => rep(Routes.invite, { organizationId }),
  privacyCenter: {
    editPrivacySection: (sectionId) => rep(Routes.privacyCenter.editPrivacySection, { sectionId }),
    editPrivacyIntro: (introId) => rep(Routes.privacyCenter.editPrivacyIntro, { introId }),
  },
  requests: {
    detail: (requestId) => rep(Routes.requests.detail, { requestId }),
    privacyEmails: () => Routes.requests.privacyEmails,
    privacyEmailConversation: (conversationId) =>
      rep(Routes.requests.privacyEmailConversation, { conversationId }),
    privacyEmailSettings: () => Routes.requests.privacyEmailSettings,
  },
  records: {
    lookup: () => Routes.records.recordLookup,
  },
  hrCenter: {
    hrDataMap: {
      dataFlowsCollectionForGroup: (groupId) =>
        rep(Routes.hrCenter.hrDataMap.dataFlowsCollection, { groupId }),
      dataFlowsDisclosuresForGroup: (groupId) =>
        rep(Routes.hrCenter.hrDataMap.dataFlowsDisclosures, { groupId }),
      dataFlowsPurposesForGroup: (groupId) =>
        rep(Routes.hrCenter.hrDataMap.dataFlowsPurposes, { groupId }),
    },
  },
  consumer: {
    detail: (consumerDetails: ConsumerDetailPageParams) =>
      `${formatUrl(Routes.consumer.detail, consumerDetails)}`,
  },
  documents: {
    dataProtectionAssessment: {
      write: (dpaId) => rep(Routes.documents.dataProtectionAssessment.write, { dpaId }),
      addApprovalParties: (dpaId) =>
        rep(Routes.documents.dataProtectionAssessment.addApprovalParties, { dpaId }),
      gatherSignatures: (dpaId) =>
        rep(Routes.documents.dataProtectionAssessment.gatherSignatures, { dpaId }),
    },
  },
  dataMap: {
    dataSourcesTab: () => `${Routes.dataMap.root}?tab=data_sources`,
    dataFlows: {
      collectionForGroup: (groupId) => rep(Routes.dataMap.dataFlows.collection, { groupId }),
      disclosuresForGroup: (groupId) => rep(Routes.dataMap.dataFlows.disclosures, { groupId }),
    },
    collectionGroup: {
      workflow: {
        collection: (dataSubjectTypeId) =>
          rep(Routes.dataMap.collectionGroup.workflow.collection, {
            dataSubjectTypeId,
          }),
        dataRecipientAssociation: (dataSubjectTypeId) =>
          rep(Routes.dataMap.collectionGroup.workflow.dataRecipientAssociation, {
            dataSubjectTypeId,
          }),
        disclosure: (dataSubjectTypeId) =>
          rep(Routes.dataMap.collectionGroup.workflow.disclosure, {
            dataSubjectTypeId,
          }),
        sourceAssociation: (dataSubjectTypeId) =>
          rep(Routes.dataMap.collectionGroup.workflow.sourceAssociation, {
            dataSubjectTypeId,
          }),
        receipt: (dataSubjectTypeId) =>
          rep(Routes.dataMap.collectionGroup.workflow.receipt, {
            dataSubjectTypeId,
          }),
      },
    },

    dataRecipient: {
      detail: (dataRecipientId) =>
        rep(Routes.dataMap.dataRecipient.detail, {
          dataRecipientId,
        }),
      workflow: {
        classification: (dataRecipientId) =>
          rep(Routes.dataMap.dataRecipient.workflow.classification, {
            dataRecipientId,
          }),
      },
    },
    dataSource: {
      detail: (dataSourceId) =>
        rep(Routes.dataMap.dataSource.detail, {
          dataSourceId,
        }),
      workflow: {
        sourceAssociation: (dataSourceId) =>
          rep(Routes.dataMap.dataSource.workflow.groupAssociation, {
            dataSourceId,
          }),
        receipt: (dataSourceId) =>
          rep(Routes.dataMap.dataSource.workflow.receipt, {
            dataSourceId,
          }),
      },
    },
  },
};

export const RouteMatches = {
  getCompliant: {
    employeeMap: {
      employeeCollectionForGroup: new RegExp(
        `${Routes.getCompliant.employeeMap.employeeCollectionForGroup.replace(
          ":consumerGroupId",
          "([0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12})",
        )}$`,
      ),
      employeeReviewForGroup: new RegExp(
        `${Routes.getCompliant.employeeMap.employeeReviewForGroup.replace(
          ":consumerGroupId",
          "([0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12})",
        )}$`,
      ),
    },
  },
};
